import * as api from '@apis/admin-review-events';
import * as feedApi from '@apis/app-feed';
import { UserContentSummary } from '@components/user-contents/UserContentSummary';
import { ReviewDetailModal } from '@components/user-contents/ReviewDetailModal';
import { useAuthentication } from '@hooks/authentication';
import { PagenatedResponse, SearchPagenateQuery } from '@models/common';
import { UserContent } from '@models/user-contents';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Descriptions, Spin, Table, Tabs } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export const ReviewEventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>(),
    navigate = useNavigate();
  useAuthentication();

  const [candidatesQuery, setCandidatesQuery] = useState<SearchPagenateQuery>({
    page: 0,
    size: 20,
  });

  const [isReviewDetailModalOpen, setIsReviewDetailModalOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);

  const loungeCurationCategoriesQuery = useQuery({
    queryKey: [`lounge-curation-categories`],
    queryFn: () => feedApi.getLoungeCurationCategories(),
  });
  const loungeCurationCategories = loungeCurationCategoriesQuery.data?.data?.data || [];
  const loungeCurationCategoriesDict: { [key: string]: string | undefined } = {};
  loungeCurationCategories.forEach((item) => {
    loungeCurationCategoriesDict[item.type] = item.title;
  });

  const getReviewEventQuery = useQuery({
    queryKey: [`review-event`, id],
    queryFn: () => api.getReviewEvent(id!),
    enabled: !!id,
  });

  const getReviewCandidatesQuery = useQuery({
    queryKey: [`review-event-candidates`, id, candidatesQuery],
    queryFn: () => api.searchReviewCandidates(id!, candidatesQuery),
    enabled: !!id,
  });

  const getSelectedReviewsQuery = useQuery({
    queryKey: [`review-event-selected`, id],
    queryFn: () => api.getSelectedReviews(id!),
    enabled: !!id,
  });

  const selectMutation = useMutation({
    mutationFn: ({ reviewId }: { reviewId: string }) => api.selectReviewCandidate(id!, reviewId),
    onSuccess: () => {
      toast.success('리뷰가 선정되었습니다.');
      getReviewCandidatesQuery.refetch();
      getSelectedReviewsQuery.refetch();
    },
  });

  const excludeMutation = useMutation({
    mutationFn: ({ reviewId }: { reviewId: string }) => api.excludeReviewCandidate(id!, reviewId),
    onSuccess: () => {
      toast.error('리뷰가 제외되었습니다.');
      getReviewCandidatesQuery.refetch();
      getSelectedReviewsQuery.refetch();
    },
  });

  const reviewDetailQuery = useQuery({
    queryKey: ['review-detail', selectedReviewId],
    queryFn: () => api.getReviewDetail(id!, selectedReviewId!),
    enabled: !!selectedReviewId,
  });

  const reviewDetail = reviewDetailQuery.data?.data;

  const reviewEvent = getReviewEventQuery.data?.data;
  const candidate = getReviewCandidatesQuery.data?.data || ({} as PagenatedResponse<UserContent>);
  const candidateContents = getReviewCandidatesQuery.data?.data?.contents || [];
  const selectedReviews = getSelectedReviewsQuery.data?.data || [];

  useEffect(() => {
    if (!id) navigate('/review-events');
  }, [id, navigate]);

  const handleSelect = (userContentId: string) => {
    selectMutation.mutate({ reviewId: userContentId });
  };

  const handleExclude = (userContentId: string) => {
    excludeMutation.mutate({ reviewId: userContentId });
  };

  const handleOpenReviewDetail = (record: UserContent) => {
    setSelectedReviewId(record._id);
    setIsReviewDetailModalOpen(true);
  };

  const handleNavigateReview = (reviewId: string) => {
    setSelectedReviewId(reviewId);
    // No need to set isReviewDetailModalOpen as it's already open
  };

  if (getReviewEventQuery.isLoading)
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  if (!reviewEvent)
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>리뷰 이벤트를 찾을 수 없습니다.</div>
      </div>
    );

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <div className="page-header">
        <div>리뷰 이벤트 상세</div>
        <div>
          <Button onClick={() => navigate('/review-events')}>목록으로</Button>
        </div>
      </div>
      <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 72px)', overflow: 'auto' }}>
        <div
          style={{
            marginTop: '16px',
            color: '#666',
            fontSize: '12px',
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}>
          <div>생성일시: {moment(reviewEvent.createdAt).format('YYYY.MM.DD HH:mm')}</div>
          <div>수정일시: {moment(reviewEvent.updatedAt).format('YYYY.MM.DD HH:mm')}</div>
        </div>
        <Descriptions bordered column={4}>
          <Descriptions.Item label="제목" span={2}>
            {reviewEvent.title}
          </Descriptions.Item>
          <Descriptions.Item label="사진 리뷰만" span={2}>
            {reviewEvent.photoReviewOnly ? '예' : '아니오'}
          </Descriptions.Item>
          <Descriptions.Item label="설명" span={4}>
            {reviewEvent.description}
          </Descriptions.Item>
          <Descriptions.Item label="시작일" span={1}>
            {moment(reviewEvent.startDate).format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label="종료일" span={1}>
            {moment(reviewEvent.endDate).format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label="발표일" span={1}>
            {moment(reviewEvent.announcementDate).format('YYYY.MM.DD')}
          </Descriptions.Item>
        </Descriptions>
        <Tabs
          style={{ marginTop: '24px' }}
          items={[
            {
              key: 'candidates',
              label: `후보 리뷰 (${candidate.total}개)`,
              children: (
                <Table
                  columns={[
                    {
                      title: '상세',
                      width: 80,
                      align: 'center',
                      render: (record) => {
                        return <Button onClick={() => handleOpenReviewDetail(record)}>상세</Button>;
                      },
                    },
                    {
                      title: '관리',
                      width: 80,
                      align: 'center',
                      render: (record) => {
                        if (selectedReviews.find((review: UserContent) => review._id === record._id)) {
                          return (
                            <Button danger onClick={() => handleExclude(record._id)}>
                              제외
                            </Button>
                          );
                        } else {
                          return <Button onClick={() => handleSelect(record._id)}>선택</Button>;
                        }
                      },
                    },
                    {
                      title: '파트너',
                      align: 'center',
                      width: 180,
                      render: (record) => {
                        const sellerName = record?.paymentStatement?.seller?.name ?? '-';
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                            <div>{sellerName}</div>
                          </div>
                        );
                      },
                    },
                    {
                      title: '상품',
                      align: 'center',
                      width: 180,
                      render: (record) => {
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                            <div>{record?.product?.title ?? '-'}</div>
                          </div>
                        );
                      },
                    },
                    {
                      title: '리뷰 내용',
                      width: 480,
                      render: (record) => {
                        return (
                          <div style={{ width: '100%' }}>
                            <UserContentSummary
                              item={record}
                              loungeCurationCategoriesDict={loungeCurationCategoriesDict}></UserContentSummary>
                          </div>
                        );
                      },
                    },
                    {
                      title: '작성자',
                      align: 'center',
                      width: 180,
                      render: (record) => {
                        const publisher = record.publisher;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                            <div>{`${publisher?.profile?.name}\n(${publisher?.profile?.phone?.substring(
                              publisher?.profile?.phone?.length - 4,
                            )})`}</div>
                          </div>
                        );
                      },
                    },
                    {
                      title: '작성일',
                      dataIndex: 'createdAt',
                      render: (date) => moment(date).format('YYYY.MM.DD HH:mm'),
                    },
                  ]}
                  dataSource={candidateContents}
                  loading={getReviewCandidatesQuery.isLoading}
                  scroll={{ x: 'max-content', y: 'calc(100dvh - 500px)' }}
                  style={{ whiteSpace: 'pre-wrap' }}
                  pagination={{
                    current: (candidatesQuery.page || 0) + 1,
                    pageSize: candidatesQuery.size || 20,
                    total: getReviewCandidatesQuery.data?.data?.total || 0,
                    onChange: (page, pageSize) => {
                      setCandidatesQuery((prev) => ({
                        ...prev,
                        page: page - 1,
                        size: pageSize,
                      }));
                    },
                  }}
                />
              ),
            },
            {
              key: 'selected',
              label: `선정된 리뷰 (${selectedReviews.length}개)`,
              children: (
                <Table
                  columns={[
                    {
                      title: '상세',
                      width: 80,
                      align: 'center',
                      render: (record) => {
                        return <Button onClick={() => handleOpenReviewDetail(record)}>상세</Button>;
                      },
                    },
                    {
                      title: '관리',
                      align: 'center',
                      width: 80,
                      render: (record) => (
                        <Button danger onClick={() => handleExclude(record._id)}>
                          제외
                        </Button>
                      ),
                    },
                    {
                      title: '파트너',
                      align: 'center',
                      width: 180,
                      render: (record) => {
                        const sellerName = record?.paymentStatement?.seller?.name ?? '-';
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                            <div>{sellerName}</div>
                          </div>
                        );
                      },
                    },
                    {
                      title: '상품',
                      align: 'center',
                      width: 180,
                      render: (record) => {
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                            <div>{record?.product?.title ?? '-'}</div>
                          </div>
                        );
                      },
                    },
                    {
                      title: '리뷰 내용',
                      width: 480,
                      render: (record) => {
                        return (
                          <div style={{ width: '100%' }}>
                            <UserContentSummary
                              item={record}
                              loungeCurationCategoriesDict={loungeCurationCategoriesDict}></UserContentSummary>
                          </div>
                        );
                      },
                    },
                    {
                      title: '작성자',
                      align: 'center',
                      width: 180,
                      render: (record) => {
                        const publisher = record.publisher;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                            <div>{`${publisher?.profile?.name}\n(${publisher?.profile?.phone?.substring(
                              publisher?.profile?.phone?.length - 4,
                            )})`}</div>
                          </div>
                        );
                      },
                    },
                    {
                      title: '작성일',
                      dataIndex: 'createdAt',
                      render: (date) => moment(date).format('YYYY.MM.DD HH:mm'),
                    },
                  ]}
                  dataSource={selectedReviews}
                  loading={getSelectedReviewsQuery.isLoading}
                  scroll={{ x: 'max-content', y: 'calc(100dvh - 500px)' }}
                  style={{ whiteSpace: 'pre-wrap' }}
                />
              ),
            },
          ]}
        />
      </div>
      {selectedReviewId && (
        <ReviewDetailModal
          isOpen={isReviewDetailModalOpen}
          onClose={() => {
            setIsReviewDetailModalOpen(false);
            setSelectedReviewId(null);
          }}
          reviewDetail={{
            ...reviewDetail,
            isSelected: selectedReviews.find((review: UserContent) => review._id === selectedReviewId),
          }}
          onSelect={handleSelect}
          onExclude={handleExclude}
          onNavigate={handleNavigateReview}
        />
      )}
    </div>
  );
};

export default ReviewEventDetailPage;
